import { Injectable } from '@angular/core';
import { Auth, signOut } from '@angular/fire/auth';
import { CollectionReference, DocumentData, Firestore, addDoc, collection, collectionData, doc, docData, getDoc, getDocs, onSnapshot, query, updateDoc, where } from '@angular/fire/firestore';
import { getDownloadURL, getStorage, ref, uploadString } from '@angular/fire/storage';
import { Router } from '@angular/router';
import { Observable } from '@firebase/util';
import { uploadBytes, uploadBytesResumable } from 'firebase/storage';
import * as moment from 'moment';
import { BehaviorSubject } from 'rxjs';
import { take } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class SharedService {
	activePrograms$ = new BehaviorSubject<any>([]);
	months = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
	usersCollection: CollectionReference<DocumentData>;
	progressCollection: CollectionReference<DocumentData>;
	activeProgramListener: any;
	monthNames = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
	newDate: Date;

	constructor(
		private firestore: Firestore,
		private router: Router,
		private auth: Auth,
	) {
		this.usersCollection = collection(this.firestore, "users");
		this.progressCollection = collection(this.firestore, "progress");
		this.newDate = new Date();

		if (this.isLeapYear()) {
			this.months[1] = 29;
		}

		const userId = localStorage.getItem('userId');
		if (userId) {
			const activeProgramQuery = query(this.progressCollection, where('userId','==', userId));

			this.activeProgramListener = onSnapshot(activeProgramQuery, (querySnapshot) => {
				let activePrograms: any = [];

				querySnapshot.forEach(doc => {
					activePrograms.push({
						id: doc.id,
						...doc.data()
					});
				});
				const normalizedPrograms = this.normalizeAndDedupeProgress(activePrograms);
				this.activePrograms$.next(normalizedPrograms);
			});
		}
	}

	normalizeProgress(value: any) {
		let progress = Number(value);
		if (!Number.isFinite(progress)) {
			return 0;
		}

		if (progress >= 99.5) {
			progress = 100;
		}

		if (progress < 0) {
			progress = 0;
		} else if (progress > 100) {
			progress = 100;
		}

		return Math.round(progress);
	}

	getProgressTimestampValue(item: any) {
		const val = item?.lastUpdatedAt || item?.completedAt || item?.startedAt;
		if (!val) {
			return 0;
		}

		if (typeof val === 'number') {
			return val;
		}

		if (val?.toDate && typeof val.toDate === 'function') {
			return val.toDate().getTime();
		}

		if (val?.seconds) {
			return Number(val.seconds) * 1000;
		}

		if (val?._seconds) {
			return Number(val._seconds) * 1000;
		}

		const dateValue = new Date(val).getTime();
		return Number.isFinite(dateValue) ? dateValue : 0;
	}

	normalizeAndDedupeProgress(programs: any[]) {
		const latestByProgram = new Map<string, any>();

		for (let program of (programs || [])) {
			const normalized = {
				...program,
				progress: this.normalizeProgress(program?.progress)
			};

			const key = String(normalized.programId || normalized.id || '');
			if (!key) {
				continue;
			}

			const prev = latestByProgram.get(key);
			if (!prev) {
				latestByProgram.set(key, normalized);
				continue;
			}

			const prevProgress = this.normalizeProgress(prev.progress);
			const currProgress = this.normalizeProgress(normalized.progress);
			const prevTimestamp = this.getProgressTimestampValue(prev);
			const currTimestamp = this.getProgressTimestampValue(normalized);

			const shouldReplace = (currProgress > prevProgress)
				|| (currProgress === prevProgress && currTimestamp >= prevTimestamp);

			if (shouldReplace) {
				latestByProgram.set(key, normalized);
			}
		}

		return Array.from(latestByProgram.values());
	}

	getObservableRes(obs: any) {
		return new Promise((resolve) => {
			obs.subscribe((res: any) => {
				resolve(res);
			});
		});
	}

	async getJson(fileName: string) {
		try {
			const json = await import(`../../assets/json/${fileName}.json`);
			if (json) {
				return json;
			} else {
				console.log(`Json not found: ${fileName}.json`);
				return {};
			}
		} catch (err) {
			console.log(`Could not load ${fileName}`, err);
			return {};
		}
	}

	async convertObsToValue(observable: any) {
		return new Promise<any>((resolve, reject) => {
			observable.pipe(take(1)).subscribe({
				next: (result: any) => {
					resolve(result);
				},
				error: (err: any) => {
					reject(err); // <-- reject here
				},
				complete: () => {
					// Optional: if observable completes without emitting
					// resolve([]);  
				}
			})
		})
	}

	isLeapYear() {
		if (this.newDate.getFullYear() % 4 === 0) {
			return true;
		}

		return false;
	}

	getPreviousDatesInMonth(currDate: Date) {
		let prevDates: any = [];
		let currMonth = currDate.getMonth();

		for (let i = 1; i < currDate.getDate(); i += 1) {
			let formattedDateString = (i < 10 ? '0' + i : i) + "-" + (currMonth < 10 ? '0' + currMonth : currMonth) + "-" + currDate.getFullYear();
			prevDates.push(formattedDateString);
		}

		return prevDates;
	}

	areDatesOfSameMonth(dateA: Date, dateB: Date) {
		var dateAStr = (dateA.getMonth() + 1) < 10 ? "0" + dateA.getMonth() + 1 : String(dateA.getMonth() + 1);
		var dateBStr = (dateB.getMonth() + 1) < 10 ? "0" + dateB.getMonth() + 1 : String(dateB.getMonth() + 1);

		if (dateAStr === dateBStr) {
			return true;
		}

		return false;
	}

	totalDaysThisMonth() {
		return this.months[new Date().getMonth()];
	}

	getRemainingDaysThisMonth() {
		let currDate = new Date();
		let monthDays = this.months[currDate.getMonth()];
		return monthDays - currDate.getDate();
	}

	checkDateDifference(dateA: Date, dateB: Date) {
		var dateAMoment = moment(dateA);
		var dateBMoment = moment(dateB);
		return dateAMoment.diff(dateBMoment, 'days');
	}

	getPendingQuizzes(sections: any) {
		let pendingQuizzes = 0;

		if (sections && sections.length >= 1) {
			for (let section of sections) {
				if (section.type === 'menu') {
					pendingQuizzes += this.getPendingQuizzes(section.menu);
				} else if (section.type === 'quiz' && section.progress !== 100) {
					pendingQuizzes += 1;
				}
			}
		}

		return pendingQuizzes;
	}

	getPendingVideos(sections: any) {
		let pendingVideos = 0;

		if (sections && sections.length >= 1) {
			for (let section of sections) {
				if (section.type === 'menu') {
					pendingVideos += this.getPendingVideos(section.menu);
				} else if (section.type === 'video' && section.progress !== 100) {
					pendingVideos += 1;
				}
			}
		}

		return pendingVideos;
	}

	getTotalNumberOfSections(sections: any) {
		let sectionCount = 0;

		if (sections && sections.length >= 1) {
			for (let section of sections) {
				if (section.type === 'menu') {
					sectionCount += this.getTotalNumberOfSections(section.menu);
				} else {
					sectionCount += 1;
				}
			}
		}

		return sectionCount;
	}

	getDaysInRow(daysInRow: any) {
		if (daysInRow?.length >= 1) {
			let currDate = new Date();
			let [checkDate, checkMonth, checkYear] = daysInRow[daysInRow.length - 1].split("-");
			let dateToCheck = new Date(checkYear, checkMonth - 1, checkDate);

			if (this.areDatesOfSameMonth(currDate, dateToCheck) && this.checkDateDifference(currDate, dateToCheck) <= 2) {
				return daysInRow.length;
			} else {
				return 0;
			}
		}

		return 0;
	}

	getDaysLoggedThisMonth(daysLoggedHistory: any) {
		if (Object.keys(daysLoggedHistory).length >= 1) {
			let currDate = new Date();
			let monthAndYear = String((currDate.getMonth() + 1) < 10 ? "0" + Number(currDate.getMonth() + 1) : Number(currDate.getMonth() + 1)) + "-" + currDate.getFullYear();

			if (daysLoggedHistory[monthAndYear]) {
				return daysLoggedHistory[monthAndYear].length;
			}

			return 0;
		}

		return 0;
	}

	getLearningMinutesThisMonth(learningHistory: any) {
		if (Object.keys(learningHistory).length >= 1) {
			let currDate = new Date();
			let monthAndYear = String((currDate.getMonth() + 1) < 10 ? "0" + Number(currDate.getMonth() + 1) : Number(currDate.getMonth() + 1)) + "-" + currDate.getFullYear();

			if (learningHistory[monthAndYear]) {
				return learningHistory[monthAndYear];
			}

			return 0;
		}

		return 0;
	}

	getLearningMinutesOverall(sections: any) {
		let totalSeconds = 0;

		for (let section of sections) {
			for (let unit of section.menu) {
				if (unit.type === "video" && unit?.metadata?.topics) {
					for (let topic of unit.metadata.topics) {
						if (topic.learningDuration) {
							totalSeconds += topic.learningDuration;
						}
					}
				}
			}
		}

		return totalSeconds;
	}

	secondsToHours(duration: any) {
		// console.log('duration', duration)
		return ((duration / 60) / 60).toFixed(1);
	}

	secondsToMinutes(duration: any) {
		return (duration / 60).toFixed(1);
	}

	isSectionCompleted(sectionNo: number, sections: any): boolean {
		if (sections?.length >= 1) {
			for (let section of sections) {
				if (section.type === 'menu') {
					if (section.menu.length >= 1) {
						return this.isSectionCompleted(sectionNo, section.menu);
					}
				} else if ((section.type === 'video' || section.type === 'quiz') && section.sno === sectionNo) {
					if (section.progress === 100) {
						return true;
					}
					return false;
				}
			}
		}

		return false;
	}

	async getIdByEmail(email: string) {
		try {
			const userQuery = query(this.usersCollection, where("email", "==", email));
			const userQuerySnap = await getDocs(userQuery);

			if (!userQuerySnap.empty) {
				return userQuerySnap.docs[0].id;
			}

			return "";
		} catch (err) {
			console.log('Unable to get id by email', err);
		}
	}

	getTotalLearningHrs(sections: any) {
		let learningHrs = 0;

		if (sections?.length >= 1) {
			for (let section of sections) {
				if (section.type === 'menu') {
					learningHrs += this.getTotalLearningHrs(section.menu);
				} else if (section.type === 'video' && section?.metadata?.topics) {
					for (let topic of section.metadata.topics) {
						learningHrs += Number(topic.videoDuration);
					}
				}
			}
		}

		return learningHrs;
	}

	async getUrlForUploadedFile(base64: string, route: string) {
		return new Promise<string>(async (resolve, reject) => {
			const storage = getStorage();
			const storageRef = ref(storage, route);
			uploadString(storageRef, base64, 'data_url').then((snapshot) => {
				getDownloadURL(storageRef).then((url) => {
					resolve(url);
				});
			});
		});
	}

	getRoleJobs(roleId: string) {
		try {
			const roleDocRef = doc(this.firestore, `roles/${roleId}`);
			return docData(roleDocRef);
		} catch (err) {
			console.log('Unable to get role jobs', err);
		}
	}

	async getActivePrograms(userId: string) {
		try {
			const activeQuery = query(this.progressCollection, where('userId', '==', userId));
			const activeSnap = await getDocs(activeQuery);

			let programs: any = [];
			activeSnap.forEach(doc => {
				programs.push({
					id: doc.id,
					...doc.data()
				});
			});

			return programs;
		} catch (err) {
			console.log('Unable to get active programs', err);
		}
	}

	getFormattedCourseTime(courseTime: any) {
		if (!courseTime.includes(":")) {
			return "00:00";
		}
		let formattedCourseTime;
		const [hours, minutes] = courseTime.split(":");
		if (hours.length === 1) {
			formattedCourseTime = `0${hours}`;
		} else {
			formattedCourseTime = hours;
		}

		if (minutes.length === 1) {
			formattedCourseTime += `:0${minutes}`;
		} else {
			formattedCourseTime += `:${minutes}`;
		}

		return formattedCourseTime;
	}

	async uploadHireJson(json: any) {
		let jsonCopy: any = {};
		for (let [key, value] of Object.entries(json)) {
			if (key != 'default') {
				jsonCopy[key] = value;
			}
		}
		json = jsonCopy;
		json.category = {
			id: "4LvZEpxRxXm8QBcnQ45s",
			name: "Management"
		};
		json.subcategory = {
			id: "892382339",
			name: "Recruitment"
		};
		json.courseTime = "01:58";
		json.difficultyLevel = "hard";
		json.description = "In a world of recruitment that's vanilla, you need to be pistachio to stand out! Learn how. Poor Recruitment is very costly. Learn the Must Do's & How To's in this powerful & impactful program.";
		json.programImage = "";
		json.landscapeImage = "";
		json.numberOfSection = 3;
		json.programMenuCompleted = true;

		for (let i = 0; i < 3; i += 1) {
			if (i == 0) {
				json.sections[i].numberOfUnits = 10;
			}

			if (i == 1) {
				json.sections[i].numberOfUnits = 6;
			}

			if (i == 2) {
				json.sections[i].numberOfUnits = 8;
			}
		}

		await addDoc(collection(this.firestore, 'programs'), json);
	}

	async getRoleData(roleId: string) {
		return new Promise(async (resolve) => {
			const roleDocRef = doc(collection(this.firestore, `roles`), roleId);
			const roleDocSnap = await getDoc(roleDocRef);
			resolve(roleDocSnap.data() || false);
		}).catch(err => {
			console.log('Unable to get role data.', err);
			return false;
		});
	}

	async checkOrganizationPageAccess(email: string) {
		return new Promise(async (resolve) => {
			const roleId = localStorage.getItem("roleId") || '';
			// console.log("roleId", roleId);
			if (roleId) {
				const roleData: any = await this.getRoleData(roleId);
				// console.log("roleData", roleData);
				if (roleData) {
					// ? Show specific organization other roles
					if ((email === 'igniteadmin1@example.com' || email === 'igniteadmin2@example.com' || email === 'devadmin1@example.com' || email === 'devadmin2@example.com' || email === 'terry@terryhawkins.com') && roleData.name === 'admin') {
						// Show all organizations if 'super admin';
						this.router.navigate(['management']);
						resolve(true);
					} else {
						this.router.navigate(['tabs'], {
							state: {
								organisationId: roleData.organisationId,
							}
						});
						resolve(false);
					}
				}
			}
			else {
				resolve(true);
			}
		}).catch(err => {
			console.log("checkOrganizationPageAccess error:", err);
			return true;
		})
	}

	async logOut() {
		try {
			localStorage.clear();
			await signOut(this.auth);
			await this.router.navigate(["login"]);
		} catch (err) {
			console.log("logOut err", err);
		}
	}

	areArraysEqual(array1: any, array2: any) {
		if (array1.length !== array2.length) {
			return false;
		}

		for (let i = 0; i < array1.length; i += 1) {
			if (array1[i] !== array2[i]) {
				return false;
			}
		}

		return true;
	}

	async getProgressWithUserIds(userIds: any) {
		try {
			let progressData: any = [];

			if (userIds.length >= 30) {
				// let userIdsArray = [];
				// let elementsSliced = 0;

				// while (elementsSliced < userIds.length) {
				// 	let slicedPart = userIds.slice(elementsSliced, elementsSliced + 30);
				// 	userIdsArray.push(slicedPart);
				// 	elementsSliced += 30;
				// }

				// for (let element of userIdsArray) {
				// 	const userQuery = query(collection(this.firestore, 'progress'), where('userId', 'in', element));
				// 	const userQuerySnap = await getDocs(userQuery);
				// 	userQuerySnap.forEach((userDoc: any) => {
				// 		progressData.push({
				// 			id: userDoc.id,
				// 			...userDoc.data()
				// 		});
				// 	});
				// }

				const userQuery = query(collection(this.firestore, 'analytics'));
				const userQuerySnap = await getDocs(userQuery);

				userQuerySnap.forEach((progressDoc: any) => {
					const data = progressDoc.data();
					if (userIds.includes(data.userId)) {
						progressData.push({
							id: progressDoc.id,
							...data
						});
					}
				});
			} else {
				const userQuery = query(collection(this.firestore, 'analytics'), where('userId', 'in', userIds));
				const userQuerySnap = await getDocs(userQuery);
				userQuerySnap.forEach((userDoc: any) => {
					progressData.push({
						id: userDoc.id,
						...userDoc.data()
					});
				});
			}

			return progressData;
		} catch (err) {
			console.log('Unable to get progress for list of user ids', err);
		}
	}

	getDosesCompletedForProgram(programData: any) {
		let dosesCompleted = 0;
		if (programData.dosesCompleted && Object.keys(programData.dosesCompleted).length >= 1) {
			for (let monthKey of Object.keys(programData.dosesCompleted)) {
				for (let dayKey of Object.keys(programData.dosesCompleted[monthKey])) {
					if (programData.dosesCompleted[monthKey][dayKey]) {
						dosesCompleted += Number(programData.dosesCompleted[monthKey][dayKey]);
					}
				}
			}
		}
		return dosesCompleted;
	}

	getDosesCompletedForProgramMonth(programData: any, month: any) {
		let dosesCompleted = 0;
		if (programData.dosesCompleted && programData.dosesCompleted[month]) {
			for (let dayKey of Object.keys(programData.dosesCompleted[month])) {
				if (programData.dosesCompleted[month][dayKey]) {
					dosesCompleted += Number(programData.dosesCompleted[month][dayKey]);
				}
			}
		}
		return dosesCompleted;
	}

	getLastSection(sections: any): any {
		let lastSection: any;

		if (sections?.length >= 1) {
			for (let section of sections) {
				if (section.type === 'menu') {
					const lastSec = this.getLastSection(section.menu);
					lastSection = lastSec;
				} else {
					lastSection = section;
				}
			}
		}

		return lastSection;
	}

	getFirstPendingSection(sections: any): any {
		if (sections?.length >= 1) {
			for (let section of sections) {
				if (section.type === 'menu') {
					const firstPendingSection = this.getFirstPendingSection(section.menu);

					if (firstPendingSection && Object.keys(firstPendingSection)?.length >= 1) {
						return firstPendingSection;
					} else {
						continue;
					}
				} else if (section.progress !== 100) {
					return section;
				}
			}
		}
	}

	getDosesCompleted(activePrograms: any) {
		let dosesCompleted = 0;
		if (activePrograms.length >= 1) {
			for (let program of activePrograms) {
				if (program.progress === 100) {
					const lastSection = this.getLastSection(program.sections);
					dosesCompleted += lastSection?.dfs;
				} else {
					const firstPendingSection = this.getFirstPendingSection(program.sections);
					// console.log(firstPendingSection);
					dosesCompleted += Number(firstPendingSection?.dfs) - 1;
				}
			}
		}

		if (dosesCompleted >= 1) {
			return dosesCompleted;
		}
		return dosesCompleted;
	}

	async getCategoryDetails(categoryId: string) {
		try {
			const catQuery = await getDoc(doc(this.firestore, `categories/${categoryId}`));
			return {
				id: catQuery.id,
				...catQuery.data()
			};
		} catch (err) {
			console.log('Unable to get category details', err);
		}
	}

	getCurrentMonthName() {
		return this.monthNames[this.newDate.getMonth()];
	}

	getCurrentYearString() {
		return String(this.newDate.getFullYear());
	}

	getCurrentMonth() {
		if ((this.newDate.getMonth() + 1) <= 9) {
			return `0${(this.newDate.getMonth() + 1)}`;
		} else {
			return `${(this.newDate.getMonth() + 1)}`;
		}
	}

	getCurrentMonthAndYear() {
		let dateString = "";
		const newDate = new Date();

		if ((newDate.getMonth() + 1) <= 9) {
			dateString += `0${(newDate.getMonth() + 1)}`;
		} else {
			dateString += `${(newDate.getMonth() + 1)}`;
		}

		dateString += `-${newDate.getFullYear()}`;
		return dateString;
	}

	getCurrentMonthAndYearObj() {
		const newDate = new Date();
		return {
			month: newDate.getMonth() + 1,
			year: newDate.getFullYear()
		};
	}

	getCurrentDate() {
		const date = String(this.newDate.getDate());

		if (date.length === 1) {
			return `0${date}`;
		}

		return date;
	}

	async getProgramData(programId: string) {
		try {
			const programDataObs = docData(doc(this.firestore, `programs/${programId}`));
			const programData = await this.getObservableRes(programDataObs);
			return programData;
		} catch (err) {
			console.log('Unable to get program data', err);
		}
	}

	async saveLastAccessTime(userId: string) {
		if (userId) {
			await updateDoc(doc(this.usersCollection, userId), {
				lastAccessTime: new Date()
			});
		}
	}

	secondsToHms(d: any) {
		d = Number(d);
		var h = Math.floor(d / 3600);
		var m = Math.floor(d % 3600 / 60);

		var hDisplay = h > 0 ? h + "hr " : "";
		var mDisplay = m > 0 ? m + "m" : "";
		return hDisplay + mDisplay;
	}

	isDateOfCurrentMonth(date: any) {
		const currentMonth = this.newDate.getMonth() + 1;
		const currentYear = this.newDate.getFullYear();

		let checkDate;
		if (date.seconds) {
			checkDate = new Date(date.seconds * 1000);
		} else if (date._seconds) {
			checkDate = new Date(date._seconds * 1000);
		} else {
			checkDate = date;
		}

		if (checkDate.getMonth() + 1 === currentMonth && checkDate.getFullYear() === currentYear) {
			return true;
		}

		return false;
	}

	getPreviousDates(num: number) {
		let dateArr: any = [];

		for (let i = 0; i < num; i += 1) {
			let subDateMoment = moment().subtract(i + 1, 'days');
			let dtObj = {
				date: subDateMoment.toDate(),
				fmt: subDateMoment.format("DD-MM-Y"),
				monthYear: subDateMoment.format("MM-Y")
			};
			dateArr.push(dtObj);
		}

		return dateArr;
	}

	getMonthsFromCurrent(date: any) {
		let dateObj;
		if (date._seconds) {
			dateObj = new Date(date._seconds * 1000);
		} else {
			dateObj = new Date(date.seconds * 1000);
		}

		let currMonthAndYear = this.getCurrentMonthAndYearObj();
		let dateObjMonthYear = this.getDateMonthAndYearObj(dateObj);

		let monthCount = 0;

		while (currMonthAndYear.month !== dateObjMonthYear.month || currMonthAndYear.year !== dateObjMonthYear.year) {
			if (dateObjMonthYear.month === 12) {
				dateObjMonthYear.month = 1;
				dateObjMonthYear.year += 1;
			} else {
				dateObjMonthYear.month += 1;
			}
			monthCount += 1;
		}
		return monthCount + 1;
	}

	getDateMonthAndYearObj(dateObj: any) {
		return {
			month: dateObj.getMonth() + 1,
			year: dateObj.getFullYear()
		};
	}

	getUserPodcastDoses(userDetails: any) {
		let podcastDoses = 0;

		if (userDetails && userDetails.miniClipsData && userDetails.miniClipsData.length >= 1) {
			let mcData = userDetails.miniClipsData;

			for (let item of mcData) {
				let data = item.miniClipData;

				for (let clip of data) {
					if (clip.completed) {
						podcastDoses += 1;
					}
				}
			}
		}

		return podcastDoses;
	}

	getUserMiniclipDoses(userDetails: any) {
		let miniclipDoses = 0;

		if (userDetails && userDetails.miniClipsData && userDetails.miniClipsData.length >= 1) {
			let mcData = userDetails.miniClipsData;

			for (let item of mcData) {
				let data = item.miniClipData;

				for (let clip of data) {
					if (clip.completed) {
						miniclipDoses += 1;
					}
				}
			}
		}

		return miniclipDoses;
	}

	async getBase64FromFile(file: any) {
		return new Promise((resolve, reject) => {
		  const reader = new FileReader();
		  reader.readAsDataURL(file);
		  reader.onload = () => {
			resolve(reader.result);
		  };
		  reader.onerror = (err) => {
			reject(err);
		  };
		});
	}

	async getUrlForUploadedVideo(file: any, route: string) {
		return new Promise<string>(async (resolve) => {
		  const storage = getStorage();
		  const storageRef = ref(storage, route);
		  uploadBytes(storageRef, file).then((snapshot) => {
			// console.log("snapshot", snapshot);
			getDownloadURL(storageRef).then((url) => {
			//   console.log("url", url);
			  resolve(url);
			}).catch(e => {
			  console.log("getDownloadURL error", e);
			  resolve('');
			});

		  }).catch(e => {
			console.log("uploadString error", e);
			resolve('');
		  });

		}).catch(e => {
		  console.log("getUrlForUploadedFile error: ", e);
		  return '';
		});
	}

	ngOnDestroy() {
		this.activeProgramListener();
	}
}
